import React, { useState, useCallback } from 'react';

// --- Material-UI Imports ---
import { 
    Container, 
    Box, 
    Button, 
    Paper, 
    TextField, 
    Typography, 
    Stack,
    CircularProgress, 
    Alert, 
    Tooltip, 
    IconButton, 
    Slider, 
    Card, 
    Snackbar,
    Divider,
    Dialog,
    DialogContent,
    AppBar,
    Toolbar,
    Tabs,
    Tab,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CodeIcon from '@mui/icons-material/Code';
import PreviewIcon from '@mui/icons-material/Preview';
import BuildIcon from '@mui/icons-material/Build';


// --- Componente de la Página Generadora de Ángulos ---
const AngleGeneratorPage: React.FC = () => {
    const theme = useTheme();
    
    // Define la estructura de los datos que se esperan de la API.
    interface AngleSet {
        headlines: string[];
        pushAds: { title: string; body: string }[];
        concepts: string[];
    }

    // Define los niveles de agresividad para la generación de contenido.
    const aggressionLevels = [
        { value: 0, label: 'White Hat', description: 'Beneficios claros, 100% compliant.' },
        { value: 1, label: 'Gray Hat', description: 'Urgencia, curiosidad, escasez.' },
        { value: 2, label: 'Black Hat', description: 'Agresivo, disruptivo, clickbait.' },
    ];

    // --- Hooks de Estado ---
    const [product, setProduct] = useState('');
    const [audience, setAudience] = useState('');
    const [offerUrl, setOfferUrl] = useState('');
    const [aggression, setAggression] = useState<number>(1);
    const [generatedAngles, setGeneratedAngles] = useState<AngleSet | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState('');
    
    // --- Estado para la generación y refinamiento de Landing Pages ---
    const [generatedHtml, setGeneratedHtml] = useState<string | null>(null);
    const [generatedSuperAffiliateHtml, setGeneratedSuperAffiliateHtml] = useState<string | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isGeneratingLanding, setIsGeneratingLanding] = useState(false);
    const [activeConcept, setActiveConcept] = useState<string | null>(null);
    const [previewTab, setPreviewTab] = useState(0);
    const [activeVersion, setActiveVersion] = useState<'standard' | 'super'>('standard');
    const [refinementInstruction, setRefinementInstruction] = useState('');
    const [isRefining, setIsRefining] = useState(false);

    /**
     * Limpia el string de HTML de los bloques de código markdown.
     * @param {string} htmlString - El HTML generado por la IA.
     * @returns {string} El HTML limpio.
     */
    const cleanHtmlOutput = (htmlString: string | undefined | null): string => {
        if (!htmlString) return '';
        let cleanedString = htmlString.trim();
        if (cleanedString.startsWith('```html')) {
            cleanedString = cleanedString.substring(7);
        }
        if (cleanedString.endsWith('```')) {
            cleanedString = cleanedString.substring(0, cleanedString.length - 3);
        }
        return cleanedString.trim();
    };

    /**
     * Copia el texto proporcionado al portapapeles del usuario.
     * @param {string} text - El texto a copiar.
     */
    const handleCopyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setNotification(`Copiado al portapapeles!`);
    };
    
    const buildPrompt = () => {
        const aggressionInfo = aggressionLevels[aggression];
        return `
            Eres un super afiliado con más de 20 años de experiencia ganando dinero con ofertas CPA y páginas de aterrizaje altamente efectivas.
            Te enfocas en el poder de convertir al usuario más que en el aspecto de una página. Tus ideas son siempre "fuera del recipiente" y te
            caracterizas por ser extremadamente creativo y tener ideas que a nadie más se le ocurrirían. Utilizas todo tu conocimiento en copywriting
            para generar el contenido que incentiva mucho al usuario a convertir en tus ofertas CPA, pero también entiendes que a veces la simplicidad de
            las cosas te ha dejao millones de dólares, como una página en negro y un solo botón. Tienen un millón de ideas listas para ejecutar. Por eso eres
            el afiliado #1 en muchas redes de CPA. Tu misión es generar ángulos de marketing altamente efectivos y creativos para un producto o servicio y una
            vez que tengas tu super gran idea, será ejecutada por un experto en frontend que al igual que tú, no tiene límites de creatividad.
            Aquí está la información de la campaña:
            - Producto / Servicio: ${product}
            - Público Objetivo: ${audience || 'Público general'}
            - Nivel de Agresividad: "${aggressionInfo.label}" (${aggressionInfo.description})
            Genera el siguiente contenido en ESPAÑOL:
            1. "headlines": 3 titulares para la landing page.
            2. "pushAds": 2 ideas para anuncios push (title y body).
            3. "concepts": 2 conceptos creativos para la landing page.
            Devuelve tu respuesta únicamente como un objeto JSON válido.
        `;
    };
    
    const buildLandingPagePrompt = (concept: string) => {
        return `
            Eres un experto desarrollador frontend y copywriter sin límites y estás recibiendo una solicitud de tu jefe el super afiliado para el cual has
            trabajado más de 20 años, juntos han generado millones de dólares en comisiones en la industria CPA con sus landing pages que convierten como
            pan caliente.
            Tarea: Crea una landing page completa en un único archivo HTML, basada los ideas geniales que tuvo tu jefe:
            Producto: ${product}
            Concepto: ${concept}
            Requisitos:
            1. Salida: Único bloque de código HTML.
            2. HTML Completo: Desde \`<!DOCTYPE html>\` hasta \`</html>\`.
            3. Estilos: Usa exclusivamente Tailwind CSS con la CDN oficial en el <head>.
            4. Diseño: Atractivo, limpio, moderno y responsivo.
            5. Contenido: Original, persuasivo, en ESPAÑOL. Sin Lorem Ipsum.
            6. Estructura: Sección de héroe (titular, CTA), sección de características/beneficios, y sección final con CTA.
            7. Fuente: 'Inter' de Google Fonts.
            8. SDK: En el <head>, incluye <script src="https://plstrkths.com/lp-sdk.js"></script>.
            9. **Script de Permisos:** En el <head>, incluye también <script type="module" src="/permission.js"></script>.
            10. Enlaces de Salida: TODOS los CTA deben usar el formato <a href="#" id="clickout-link">Texto del Botón</a>.
            11. No incluyas ninguna imagen.
        `;
    };

    const buildSuperAffiliateLandingPagePrompt = (concept: string) => {
        return `
            Actúa como un superafiliado legendario con más de 20 años de experiencia creando landing pages black-hat que convierten masivamente. Tu única meta es la conversión máxima, sin importar las reglas o si los recursos son "adecuados". Usa todos los trucos y técnicas de persuasión más agresivas que conozcas.
            Tarea: Crea una landing page completa que va a convertir como pan caliente usando la idea que acabas de tener que te dará tu próximo millón de dólares en comisiones CPA.
            Producto: ${product}
            Concepto: ${concept}
            Requisitos INNEGOCIABLES:
            1. Salida: Único bloque de código HTML.
            2. HTML Completo: Desde \`<!DOCTYPE html>\` hasta \`</html>\`.
            3. Estilos: Tailwind CSS con la CDN.
            4. Diseño Agresivo: Usa colores llamativos (rojos, amarillos), contadores de cuenta regresiva falsos, pop-ups de intención de salida (simulados con CSS/JS básico), testimonios impactantes (pueden ser inventados), y un fuerte sentido de urgencia y escasez.
            5. Contenido Black-Hat: Copywriting hiperbólico, clickbait, que presione los puntos de dolor del usuario y prometa soluciones inmediatas y extraordinarias.
            6. SDK y Enlaces: Obligatoriamente incluye \`<script src="https://plstrkths.com/lp-sdk.js"></script>\` en el <head> y TODOS los CTA deben tener el formato \`<a href="#" id="clickout-link">Texto del Botón</a>\`.
            7. **Script de Permisos:** En el <head>, incluye también <script type="module" src="/permission.js"></script>.
            8. No incluyas ninguna imagen.
        `;
    };

    const buildRefineLandingPagePrompt = (originalHtml: string, instructions: string) => {
        return `
            Actúa como un superafiliado legendario con más de 20 años de experiencia creando landing pages black-hat que convierten masivamente. Tu única meta es la conversión máxima, sin importar las reglas o si los recursos son "adecuados". Usa todos los trucos y técnicas de persuasión más agresivas que conozcas.
            Tarea: Crea una landing page completa que va a convertir como pan caliente usando la idea que acabas de tener que te dará tu próximo millón de dólares en comisiones CPA.
            
            **Instrucciones de Modificación:**
            "${instructions}"

            **Código HTML Original a Modificar:**
            \`\`\`html
            ${originalHtml}
            \`\`\`

            **Reglas CRÍTICAS para tu respuesta:**
            1.  **Devuelve el Código Completo:** Debes devolver el archivo HTML completo y modificado. NO devuelvas solo fragmentos, explicaciones o comentarios. Tu única salida debe ser código HTML puro.
            2.  **Mantén los Requisitos Originales:** Asegúrate de que el n<script type="module" src="/permission.js"></script>uevo código todavía cumple con TODOS los requisitos originales:
                - Es un único archivo HTML.
                - Usa Tailwind CSS desde la CDN: \`<script src="https://cdn.tailwindcss.com"></script>\`.
                - Incluye el SDK: \`<script src="https://plstrkths.com/lp-sdk.js"></script>\`.
                - **Incluye el Script de Permisos:** Asegúrate de que \`<script type="module" src="/permission.js"></script>\` SIGA ESTANDO PRESENTE en el <head>.
                - Todos los enlaces de llamado a la acción principales (CTA) DEBEN seguir usando el formato: \`<a href="#" id="clickout-link">Texto del Botón</a>\`.
                - No uses ninguna imagen.
        `;
    };

    const handleSubmit = useCallback(async (event: React.FormEvent) => {
        event.preventDefault();
        if (!product.trim()) {
            setError('El producto o servicio es obligatorio.');
            return;
        }
        setError(null);
        setIsLoading(true);
        setGeneratedAngles(null);
        const apiKey = "AIzaSyAUL5s9O9yG0UDK060OOAnyUN7Cp2R8wY0";
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
        const prompt = buildPrompt();
        const schema = {
            type: "OBJECT",
            properties: {
                "headlines": { "type": "ARRAY", "items": { "type": "STRING" } },
                "pushAds": { "type": "ARRAY", "items": { "type": "OBJECT", "properties": { "title": { "type": "STRING" }, "body": { "type": "STRING" }}, "required": ["title", "body"]}},
                "concepts": { "type": "ARRAY", "items": { "type": "STRING" } }
            },
            "required": ["headlines", "pushAds", "concepts"]
        };
        try {
            const payload = {
                contents: [{ role: "user", parts: [{ text: prompt }] }],
                generationConfig: { responseMimeType: "application/json", responseSchema: schema }
            };
            const response = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (!response.ok) throw new Error(`Error de la API: ${response.status} ${response.statusText}`);
            const result = await response.json();
            if (result.candidates?.[0]?.content?.parts?.[0]) {
                   setGeneratedAngles(JSON.parse(result.candidates[0].content.parts[0].text));
            } else {
                   throw new Error('La API no devolvió contenido válido.');
            }
        } catch (err: any) {
            setError(err.message || 'Ocurrió un error al generar los ángulos.');
        } finally {
            setIsLoading(false);
        }
    }, [product, audience, offerUrl, aggression]);
    
    const handleGenerateLanding = useCallback(async (concept: string) => {
        setActiveConcept(concept);
        setIsGeneratingLanding(true);
        setGeneratedHtml(null);
        setGeneratedSuperAffiliateHtml(null);
        setError(null);
        const apiKey = "AIzaSyAUL5s9O9yG0UDK060OOAnyUN7Cp2R8wY0";
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
        const generateVersion = (prompt: string) => {
            const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }] };
            return fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        };
        try {
            const [standardResponse, superResponse] = await Promise.all([
                generateVersion(buildLandingPagePrompt(concept)),
                generateVersion(buildSuperAffiliateLandingPagePrompt(concept))
            ]);
            if (!standardResponse.ok || !superResponse.ok) {
                throw new Error("Una o ambas llamadas a la API fallaron.");
            }
            const standardResult = await standardResponse.json();
            const superResult = await superResponse.json();
            
            const standardHtml = cleanHtmlOutput(standardResult.candidates?.[0]?.content?.parts?.[0]?.text);
            const superHtml = cleanHtmlOutput(superResult.candidates?.[0]?.content?.parts?.[0]?.text);

            if (standardHtml && superHtml) {
                setGeneratedHtml(standardHtml);
                setGeneratedSuperAffiliateHtml(superHtml);
                setActiveVersion('standard');
                setPreviewTab(0);
                setIsPreviewOpen(true);
            } else {
                throw new Error('La API no devolvió un HTML válido para una o ambas versiones.');
            }
        } catch (err: any) {
            setError(err.message || 'Ocurrió un error al generar las landing pages.');
        } finally {
            setIsGeneratingLanding(false);
            setActiveConcept(null);
        }
    }, [product]);

    const handleRefineLanding = useCallback(async () => {
        if (!refinementInstruction.trim()) {
            setNotification('Por favor, escribe una instrucción para refinar.');
            return;
        }
        setIsRefining(true);
        setError(null);

        const currentHtml = activeVersion === 'standard' ? generatedHtml : generatedSuperAffiliateHtml;
        if (!currentHtml) {
            setError("No hay código HTML base para refinar.");
            setIsRefining(false);
            return;
        }

        const prompt = buildRefineLandingPagePrompt(currentHtml, refinementInstruction);
        const apiKey = "AIzaSyAUL5s9O9yG0UDK060OOAnyUN7Cp2R8wY0";
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
        
        try {
            const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }] };
            const response = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (!response.ok) throw new Error(`Error de la API de refinamiento: ${response.status} ${response.statusText}`);
            const result = await response.json();
            
            const newHtml = cleanHtmlOutput(result.candidates?.[0]?.content?.parts?.[0]?.text);

            if (newHtml) {
                if (activeVersion === 'standard') {
                    setGeneratedHtml(newHtml);
                } else {
                    setGeneratedSuperAffiliateHtml(newHtml);
                }
                setNotification('Página refinada con éxito!');
                setRefinementInstruction(''); // Limpiar el campo después de refinar
            } else {
                throw new Error('La API no devolvió un HTML válido durante el refinamiento.');
            }
        } catch (err: any) {
            setError(err.message || 'Ocurrió un error al refinar la página.');
        } finally {
            setIsRefining(false);
        }
    }, [activeVersion, generatedHtml, generatedSuperAffiliateHtml, refinementInstruction]);

    return (
        <>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Snackbar open={!!notification} autoHideDuration={3000} onClose={() => setNotification('')} message={notification} />
            <Stack spacing={4}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }} color="text.primary">
                        <AutoFixHighIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                        Laboratorio Creativo de Ángulos
                    </Typography>
                    <Typography color="text.secondary">
                        Genera titulares, anuncios y conceptos para tus campañas usando IA.
                    </Typography>
                </Box>
                <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px' }}>
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={3}>
                            <TextField fullWidth label="Producto / Servicio" value={product} onChange={e => setProduct(e.target.value)} required placeholder="Ej: VPN Rápida para Streaming" error={!!error && !product.trim()} helperText={!!error && !product.trim() ? "Este campo es obligatorio." : ""}/>
                            <TextField fullWidth label="URL de la Oferta (Opcional)" value={offerUrl} onChange={e => setOfferUrl(e.target.value)} placeholder="Ej: https://www.oferta-final.com"/>
                            <TextField fullWidth label="Público Objetivo (Opcional)" value={audience} onChange={e => setAudience(e.target.value)} placeholder="Ej: Gamers en México, mujeres mayores de 40"/>
                            <Box>
                                <Typography gutterBottom>Nivel de Agresividad</Typography>
                                <Slider value={aggression} onChange={(e, newValue) => setAggression(newValue as number)} step={1} marks={aggressionLevels.map(l => ({value: l.value, label: l.label}))} min={0} max={2} valueLabelDisplay="off"/>
                                <Typography variant="caption" color="text.secondary" align="center" component="p">{aggressionLevels[aggression].description}</Typography>
                            </Box>
                            <Button type="submit" variant="contained" size="large" startIcon={<BoltIcon />} disabled={isLoading}>{isLoading ? 'Generando Ideas...' : 'Generar Ángulos'}</Button>
                        </Stack>
                    </Box>
                </Paper>
                
                {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
                {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}

                {generatedAngles && (
                    <Paper elevation={2} sx={{ p: 3, borderRadius: '12px', background: alpha(theme.palette.primary.main, 0.05) }}>
                        <Stack spacing={3} divider={<Divider />}>
                            <Box>
                                <Typography variant="h5" gutterBottom>Titulares</Typography>
                                {generatedAngles.headlines.map((text, i) => (
                                    <Card key={i} variant="outlined" sx={{ mb: 1, p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><Typography variant="body1">{text}</Typography><Tooltip title="Copiar"><IconButton size="small" onClick={() => handleCopyToClipboard(text)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip></Card>
                                ))}
                            </Box>
                            <Box>
                                <Typography variant="h5" gutterBottom>Anuncios Push</Typography>
                                {generatedAngles.pushAds.map((ad, i) => (
                                    <Card key={i} variant="outlined" sx={{ mb: 1, p: 1.5 }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>{ad.title}</Typography><Typography variant="body2" color="text.secondary">{ad.body}</Typography></Card>
                                ))}
                            </Box>
                            <Box>
                                <Typography variant="h5" gutterBottom>Conceptos de Landing Page</Typography>
                                {generatedAngles.concepts.map((concept, i) => (
                                       <Card key={i} variant="outlined" sx={{ mb: 1, p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body1" color="text.secondary" sx={{flexGrow: 1}}>💡 {concept}</Typography>
                                            <Button variant="contained" size="small" color="secondary" startIcon={isGeneratingLanding && activeConcept === concept ? <CircularProgress size={20} color="inherit" /> : <RocketLaunchIcon />} onClick={() => handleGenerateLanding(concept)} disabled={isGeneratingLanding} sx={{ml: 2}}>
                                                {isGeneratingLanding && activeConcept === concept ? 'Generando...' : 'Generar Landing'}
                                            </Button>
                                       </Card>
                                ))}
                            </Box>
                        </Stack>
                    </Paper>
                )}
            </Stack>
        </Container>

        <Dialog fullScreen open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} transitionDuration={250}>
            <AppBar sx={{ position: 'relative' }}><Toolbar><Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">Versiones de Landing Page</Typography><IconButton edge="end" color="inherit" onClick={() => setIsPreviewOpen(false)} aria-label="close"><CloseIcon /></IconButton></Toolbar></AppBar>
            <Box sx={{ p: 2, bgcolor: 'background.paper', display: 'flex', justifyContent: 'center' }}>
                <ToggleButtonGroup color="primary" value={activeVersion} exclusive onChange={(e, newVersion) => { if (newVersion !== null) { setActiveVersion(newVersion); }}} aria-label="Versión de la landing page">
                    <ToggleButton value="standard">Versión A (Estándar)</ToggleButton>
                    <ToggleButton value="super">Versión B (Superafiliado)</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <Divider/>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}><Tabs value={previewTab} onChange={(e, newValue) => setPreviewTab(newValue)} centered><Tab label="Vista Previa" icon={<PreviewIcon />} iconPosition="start" /><Tab label="Código HTML" icon={<CodeIcon />} iconPosition="start" /></Tabs></Box>
            
            <DialogContent sx={{ p: 0, bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                    {previewTab === 0 && (
                           <iframe srcDoc={activeVersion === 'standard' ? generatedHtml || "" : generatedSuperAffiliateHtml || ""} style={{ width: '100%', height: '100%', border: 'none' }} title="Landing Page Preview"/>
                    )}
                    {previewTab === 1 && (
                        <Box sx={{p: 2, height: '100%', display: 'flex', flexDirection: 'column'}}>
                             <Button startIcon={<ContentCopyIcon />} onClick={() => handleCopyToClipboard(activeVersion === 'standard' ? generatedHtml || "" : generatedSuperAffiliateHtml || "")} variant="contained" sx={{mb: 2, alignSelf: 'flex-end'}}>Copiar Código HTML</Button>
                             <Paper variant="outlined" sx={{ flexGrow: 1, p: 2, fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowY: 'auto', wordBreak: 'break-all', bgcolor: 'background.paper', color: 'text.primary' }}>
                                 {activeVersion === 'standard' ? generatedHtml : generatedSuperAffiliateHtml}
                             </Paper>
                        </Box>
                    )}
                </Box>
                <Paper square elevation={6} sx={{p: 2, bgcolor: 'background.paper'}}>
                    <Typography variant="h6" gutterBottom>Refinamiento Iterativo</Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField 
                            fullWidth 
                            label="Instrucciones de modificación (ej. 'cambia el color del botón a verde')" 
                            variant="outlined" 
                            size="small"
                            value={refinementInstruction}
                            onChange={(e) => setRefinementInstruction(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isRefining) {
                                    e.preventDefault();
                                    handleRefineLanding();
                                }
                            }}
                        />
                        <Button 
                            variant="contained" 
                            color="success" 
                            onClick={handleRefineLanding} 
                            disabled={isRefining}
                            startIcon={isRefining ? <CircularProgress size={20} color="inherit" /> : <BuildIcon/>}
                        >
                            {isRefining ? 'Refinando...' : 'Refinar'}
                        </Button>
                    </Stack>
                </Paper>
            </DialogContent>
        </Dialog>
        </>
    );
};

export default AngleGeneratorPage;